/*
 * HP search suggestions (as-you-type) for the approved header search.
 * Attaches to every form.hp-approved-header-search[data-hp-suggest] (desktop and mobile instance).
 * Source: the theme section sections/hp-search-suggest.liquid rendered through the Section Rendering API on
 * Shopify's predictive search route (products only, verified server-side with the same needle logic as the
 * results page). The public /search/suggest.json carries no variant SKUs, so it cannot show or verify article numbers.
 * Shows the product title and the customer-facing article number (variant SKU, "HP-...").
 * Never prices, never tags: tags carry internal ERP numbers and must not surface.
 * Keyboard: ArrowDown / ArrowUp / Home / End move, Enter opens, Escape closes, Tab leaves.
 * The list is portalled to <body> (position: fixed) so header overflow and stacking cannot clip it.
 */
(() => {
  'use strict';

  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const SHOW_LIMIT = 6;

  const forms = document.querySelectorAll('form.hp-approved-header-search[data-hp-suggest]');
  if (!forms.length) return;

  const cache = new Map();
  let uid = 0;

  const fold = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/ø/g, 'o')
      .replace(/æ/g, 'ae')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  const tokenize = (query) => fold(query).split(/[\s,;/]+/).filter((token) => token.length >= 2);

  const articleNumber = (product) => (typeof product.sku === 'string' ? product.sku.trim() : '');

  const thumbUrl = (product) => (typeof product.image === 'string' ? product.image : '');

  const highlight = (target, text, query) => {
    const source = String(text || '');
    const folded = fold(source);
    const ranges = [];
    if (folded.length === source.length) {
      for (const token of tokenize(query)) {
        let from = 0;
        while (from < folded.length) {
          const index = folded.indexOf(token, from);
          if (index === -1) break;
          ranges.push([index, index + token.length]);
          from = index + token.length;
        }
      }
    }
    ranges.sort((a, b) => a[0] - b[0]);
    let cursor = 0;
    for (const [start, end] of ranges) {
      if (start < cursor) continue;
      if (start > cursor) target.append(document.createTextNode(source.slice(cursor, start)));
      const mark = document.createElement('mark');
      mark.textContent = source.slice(start, end);
      target.append(mark);
      cursor = end;
    }
    if (cursor < source.length) target.append(document.createTextNode(source.slice(cursor)));
  };

  const fill = (template, terms) => String(template || '').split('__TERMS__').join(terms);

  class Suggest {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[name="q"]');
      if (!this.input) return;

      const data = form.dataset;
      this.i18n = {
        label: data.i18nSuggestions || 'Search suggestions',
        empty: data.i18nNoResults || 'No matches for “__TERMS__”.',
        viewAll: data.i18nViewAll || 'All results for “__TERMS__”',
        status: data.i18nStatus || '__COUNT__ suggestions for “__TERMS__”',
        article: data.i18nArticle || 'Article number',
      };
      this.suggestUrl = data.suggestUrl || '/search/suggest';
      this.searchUrl = data.searchUrl || '/search';
      this.mailto = data.mailto || '';
      this.identifyUrl = data.identifyUrl || '';
      this.identifyLabel = data.identifyLabel || 'Help identify my part';

      this.id = 'hp-suggest-' + ++uid;
      this.open = false;
      this.active = -1;
      this.items = [];
      this.lastQuery = '';
      this.timer = 0;
      this.controller = null;

      this.box = document.createElement('div');
      this.box.className = 'hp-suggest';
      this.box.id = this.id;
      this.box.hidden = true;

      this.list = document.createElement('ul');
      this.list.className = 'hp-suggest__list';
      this.list.id = this.id + '-list';
      this.list.setAttribute('role', 'listbox');
      this.list.setAttribute('aria-label', this.i18n.label);
      this.box.append(this.list);

      this.status = document.createElement('div');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      form.append(this.status);

      document.body.append(this.box);

      const input = this.input;
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-haspopup', 'listbox');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', this.list.id);
      input.setAttribute('autocomplete', 'off');

      input.addEventListener('input', () => this.onInput());
      input.addEventListener('keydown', (event) => this.onKeyDown(event));
      input.addEventListener('focus', () => {
        if (this.items.length && input.value.trim() === this.lastQuery) this.show();
      });
      form.addEventListener('focusout', (event) => {
        const next = event.relatedTarget;
        if (next && (form.contains(next) || this.box.contains(next))) return;
        this.hide();
      });
      this.box.addEventListener('pointerdown', (event) => {
        // Keep focus in the input so focusout does not close the list before the click lands.
        if (event.target.closest('a')) event.preventDefault();
      });
      this.box.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;
        const index = this.items.indexOf(option);
        if (index !== -1 && index !== this.active) this.setActive(index, false);
      });
      document.addEventListener('pointerdown', (event) => {
        if (!this.open) return;
        if (form.contains(event.target) || this.box.contains(event.target)) return;
        this.hide();
      });
      const reposition = () => {
        if (this.open) this.position();
      };
      window.addEventListener('resize', reposition);
      window.addEventListener('scroll', reposition, { passive: true });
    }

    onInput() {
      const query = this.input.value.trim();
      window.clearTimeout(this.timer);
      if (query.length < MIN_CHARS) {
        this.hide();
        this.items = [];
        this.lastQuery = '';
        return;
      }
      this.timer = window.setTimeout(() => this.lookup(query), DEBOUNCE_MS);
    }

    async lookup(query) {
      if (cache.has(query)) {
        this.render(cache.get(query), query);
        return;
      }
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      const params = new URLSearchParams();
      params.set('q', window.HPSearchQuery ? window.HPSearchQuery.normalize(query) : query);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      params.set('section_id', 'hp-search-suggest');
      try {
        const response = await fetch(this.searchUrl + '?' + params.toString(), {
          signal: controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('suggest section ' + response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('script[data-hp-suggest-json]');
        const parsed = node ? JSON.parse(node.textContent) : [];
        const relevant = (Array.isArray(parsed) ? parsed : []).filter((product) => product && product.title).sort((a, b) => Number(Boolean(b.variant)) - Number(Boolean(a.variant))).slice(0, SHOW_LIMIT);
        if (cache.size > 60) cache.delete(cache.keys().next().value);
        cache.set(query, relevant);
        if (this.input.value.trim() !== query) return;
        this.render(relevant, query);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        this.hide();
      }
    }

    render(products, query) {
      this.lastQuery = query;
      this.list.textContent = '';
      this.items = [];
      this.active = -1;

      products.forEach((product, index) => {
        const item = document.createElement('li');
        item.className = 'hp-suggest__item';
        const link = document.createElement('a');
        link.className = 'hp-suggest__option';
        link.id = this.id + '-option-' + index;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        link.href = product.url;

        const thumb = thumbUrl(product);
        if (thumb) {
          const img = document.createElement('img');
          img.className = 'hp-suggest__thumb';
          img.src = thumb;
          img.alt = '';
          img.width = 40;
          img.height = 40;
          img.loading = 'lazy';
          link.append(img);
        } else {
          const blank = document.createElement('span');
          blank.className = 'hp-suggest__thumb hp-suggest__thumb--blank';
          link.append(blank);
        }

        const text = document.createElement('span');
        text.className = 'hp-suggest__text';
        const title = document.createElement('span');
        title.className = 'hp-suggest__title';
        highlight(title, product.title, query);
        text.append(title);

        const sku = articleNumber(product);
        const type = typeof product.type === 'string' ? product.type.trim() : '';
        if (sku || type) {
          const meta = document.createElement('span');
          meta.className = 'hp-suggest__meta';
          if (sku) {
            const hidden = document.createElement('span');
            hidden.className = 'visually-hidden';
            hidden.textContent = this.i18n.article + ': ';
            const code = document.createElement('span');
            code.className = 'hp-suggest__sku';
            highlight(code, sku, query);
            meta.append(hidden, code);
          }
          if (sku && type) meta.append(document.createTextNode(' · '));
          if (type) meta.append(document.createTextNode(type));
          text.append(meta);
        }
        if (product.variant) {
          const detail = document.createElement('span');
          detail.className = 'hp-suggest__meta';
          detail.textContent = product.variant;
          text.append(detail);
        }
        link.append(text);
        item.append(link);
        this.list.append(item);
        this.items.push(link);
      });

      if (products.length) {
        const item = document.createElement('li');
        item.className = 'hp-suggest__item hp-suggest__item--all';
        const all = document.createElement('a');
        all.className = 'hp-suggest__all';
        all.id = this.id + '-option-all';
        all.setAttribute('role', 'option');
        all.setAttribute('aria-selected', 'false');
        const params = new URLSearchParams();
        params.set('q', window.HPSearchQuery ? window.HPSearchQuery.normalize(query) : query);
        params.set('type', 'product');
        params.set('options[prefix]', 'last');
        all.href = this.searchUrl + '?' + params.toString();
        all.append(document.createTextNode(fill(this.i18n.viewAll, query)));
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        all.append(arrow);
        item.append(all);
        this.list.append(item);
        this.items.push(all);
      } else {
        const item = document.createElement('li');
        item.className = 'hp-suggest__item hp-suggest__item--empty';
        const empty = document.createElement('p');
        empty.className = 'hp-suggest__empty';
        empty.textContent = fill(this.i18n.empty, query);
        if (this.identifyUrl) {
          const identify = document.createElement('a');
          identify.className = 'hp-suggest__mail';
          const target = new URL(this.identifyUrl, window.location.origin);
          target.searchParams.set('hp_search', query);
          target.hash = 'hp-custom-upload-form';
          identify.href = target.href;
          identify.textContent = this.identifyLabel;
          empty.append(document.createTextNode(' '), identify);
        } else if (this.mailto) {
          empty.append(document.createTextNode(' '));
          const mail = document.createElement('a');
          mail.className = 'hp-suggest__mail';
          mail.href = this.mailto;
          mail.textContent = this.mailto.replace(/^mailto:/, '').split('?')[0];
          empty.append(mail);
        }
        item.append(empty);
        this.list.append(item);
      }

      this.status.textContent = fill(this.i18n.status, query).split('__COUNT__').join(String(products.length));
      this.show();
    }

    setActive(index, scroll) {
      this.items.forEach((item, i) => item.setAttribute('aria-selected', i === index ? 'true' : 'false'));
      this.active = index;
      if (index >= 0) {
        const option = this.items[index];
        this.input.setAttribute('aria-activedescendant', option.id);
        if (scroll !== false) option.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKeyDown(event) {
      const count = this.items.length;
      switch (event.key) {
        case 'ArrowDown':
          if (!count) return;
          event.preventDefault();
          if (!this.open) this.show();
          this.setActive(this.active < count - 1 ? this.active + 1 : 0);
          break;
        case 'ArrowUp':
          if (!count || !this.open) return;
          event.preventDefault();
          this.setActive(this.active > 0 ? this.active - 1 : count - 1);
          break;
        case 'Home':
          if (!count || !this.open) return;
          event.preventDefault();
          this.setActive(0);
          break;
        case 'End':
          if (!count || !this.open) return;
          event.preventDefault();
          this.setActive(count - 1);
          break;
        case 'Enter':
          if (this.open && this.active >= 0 && this.items[this.active]) {
            event.preventDefault();
            window.location.href = this.items[this.active].href;
          }
          break;
        case 'Escape':
          if (this.open) {
            event.preventDefault();
            this.hide();
          }
          break;
        case 'Tab':
          this.hide();
          break;
        default:
          break;
      }
    }

    position() {
      const rect = this.form.getBoundingClientRect();
      const gutter = 12;
      const viewport = document.documentElement.clientWidth;
      const width = Math.min(Math.max(rect.width, 400), viewport - gutter * 2);
      let left = rect.left;
      if (left + width > viewport - gutter) left = Math.max(gutter, viewport - gutter - width);
      this.box.style.top = rect.bottom + 8 + 'px';
      this.box.style.left = left + 'px';
      this.box.style.width = width + 'px';
    }

    show() {
      if (!this.list.childElementCount) return;
      this.position();
      this.box.hidden = false;
      this.open = true;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      window.clearTimeout(this.timer);
      this.box.hidden = true;
      this.open = false;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.items.forEach((item) => item.setAttribute('aria-selected', 'false'));
      this.active = -1;
    }
  }

  forms.forEach((form) => new Suggest(form));
})();
